import { PagesById } from '../catalogProductsListV2.types';
import {
  DropPointerSide,
  DropTarget,
  GetIsCrossSectionDropParams,
  GetIsDrawableDropParams,
  ResolveDropTargetParams,
  ResolvePointerSideParams,
  ResolvedRow,
} from './catalogDragAndDrop.types';

const SECTION_DROPPABLE_PREFIX = 'catalog-section:';

export const buildSectionDroppableId = (sectionId: string): string =>
  `${SECTION_DROPPABLE_PREFIX}${sectionId}`;

export const parseSectionDroppableId = (droppableId: string): string | null => {
  if (!droppableId.startsWith(SECTION_DROPPABLE_PREFIX)) {
    return null;
  }

  return droppableId.slice(SECTION_DROPPABLE_PREFIX.length);
};

export const resolvePointerSide = ({
  pointerCoordinates,
  overRect,
  isListMode,
}: ResolvePointerSideParams): DropPointerSide => {
  if (!pointerCoordinates) {
    return 'after';
  }

  if (isListMode) {
    const overCenterY = overRect.top + overRect.height / 2;

    return pointerCoordinates.y < overCenterY ? 'before' : 'after';
  }

  const overCenterX = overRect.left + overRect.width / 2;

  return pointerCoordinates.x < overCenterX ? 'before' : 'after';
};

interface ItemLocation {
  sectionId: string;
  index: number;
}

interface IsNoOpMoveParams {
  sectionOrder: Map<string, string[]>;
  activeId: string;
  toSectionId: string;
  afterItemId: string | null;
}

const buildSectionOrder = (rows: ResolvedRow[]): Map<string, string[]> => {
  const sectionOrder = new Map<string, string[]>();

  for (const row of rows) {
    const orderedItemIds = sectionOrder.get(row.sectionId);
    if (orderedItemIds) {
      orderedItemIds.push(...row.itemIds);
    } else {
      sectionOrder.set(row.sectionId, [...row.itemIds]);
    }
  }

  return sectionOrder;
};

const locateItem = (sectionOrder: Map<string, string[]>, itemId: string): ItemLocation | null => {
  for (const [sectionId, orderedItemIds] of sectionOrder) {
    const index = orderedItemIds.indexOf(itemId);
    if (index !== -1) {
      return { sectionId, index };
    }
  }

  return null;
};

const getLeftSibling = (orderedItemIds: string[], index: number): string | null => {
  if (index <= 0) {
    return null;
  }

  return orderedItemIds[index - 1];
};

const isNoOpMove = ({
  sectionOrder,
  activeId,
  toSectionId,
  afterItemId,
}: IsNoOpMoveParams): boolean => {
  if (afterItemId === activeId) {
    return true;
  }

  const activeLocation = locateItem(sectionOrder, activeId);
  if (!activeLocation) {
    return false;
  }

  const activeSectionItemIds = sectionOrder.get(activeLocation.sectionId) ?? [];
  const activeLeftSibling = getLeftSibling(activeSectionItemIds, activeLocation.index);

  return toSectionId === activeLocation.sectionId && afterItemId === activeLeftSibling;
};

export const resolveDropTarget = (params: ResolveDropTargetParams): DropTarget | null => {
  const { activeId, overId, pointerSide, rows } = params;

  const overSectionId = parseSectionDroppableId(overId);
  if (overSectionId) {
    return { toSectionId: overSectionId, afterItemId: null };
  }

  const sectionOrder = buildSectionOrder(rows);

  const overLocation = locateItem(sectionOrder, overId);
  if (!overLocation) {
    return null;
  }

  const toSectionId = overLocation.sectionId;
  const targetSectionItemIds = sectionOrder.get(toSectionId) ?? [];

  const afterItemId =
    pointerSide === 'after' ? overId : getLeftSibling(targetSectionItemIds, overLocation.index);

  if (isNoOpMove({ sectionOrder, activeId, toSectionId, afterItemId })) {
    return null;
  }

  return { toSectionId, afterItemId };
};

export const getIsCrossSectionDrop = ({
  activeId,
  isManualGroupingCriterion,
  overId,
  rows,
}: GetIsCrossSectionDropParams): boolean => {
  if (isManualGroupingCriterion) {
    return false;
  }

  const overSectionId = parseSectionDroppableId(overId) ?? resolveFromSectionId(rows, overId);

  return resolveFromSectionId(rows, activeId) !== overSectionId;
};

export const buildResolvedRows = (pagesById: PagesById): ResolvedRow[] => {
  const sortedPages = [...pagesById.entries()].sort(
    ([firstPage], [secondPage]) => firstPage - secondPage,
  );

  const rows: ResolvedRow[] = [];
  let currentRow: ResolvedRow | null = null;

  for (const [, slot] of sortedPages) {
    for (const esProduct of slot.data ?? []) {
      const sectionId = esProduct.catalogSection.id;

      if (!currentRow || currentRow.sectionId !== sectionId) {
        currentRow = { sectionId, itemIds: [] };
        rows.push(currentRow);
      }

      currentRow.itemIds.push(esProduct.id);
    }
  }

  return rows;
};

export const resolveFromSectionId = (rows: ResolvedRow[], itemId: string): string | null => {
  const row = rows.find((currentRow) => currentRow.itemIds.includes(itemId));

  return row ? row.sectionId : null;
};

export const getIsDrawableDrop = ({
  activeId,
  isManualGroupingCriterion,
  overId,
  pointerSide,
  rows,
}: GetIsDrawableDropParams): boolean => {
  if (getIsCrossSectionDrop({ activeId, isManualGroupingCriterion, overId, rows })) {
    return false;
  }

  return Boolean(resolveDropTarget({ activeId, overId, pointerSide, rows }));
};
